import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { evaluateEntitlement } from '@/lib/subscriptionEntitlement';

interface PlanLimits {
  plan_tier: string;
  max_active_norths: number | null;
  max_team_members: number | null;
  includes_custom_branding: boolean;
  includes_white_label: boolean;
  includes_api_access: boolean;
  max_cmas_per_month: number | null;
  max_property_reports_per_month: number | null;
}

interface TeamUsage {
  norths_created: number;
  cmas_this_month: number;
  property_reports_this_month: number;
}

interface TeamSubscription {
  plan_tier: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  pending_downgrade_tier: string | null;
  pending_downgrade_date: string | null;
  billing_frequency?: string | null;
}

export function useTeamUsage() {
  const { toast } = useToast();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [usage, setUsage] = useState<TeamUsage | null>(null);
  const [subscription, setSubscription] = useState<TeamSubscription | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const previousUserIdRef = useRef<string | null>(null);

  const loadTeamData = async (effectiveUserId: string) => {
    try {
      // Get user's team
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('team_id, user_id')
        .eq('user_id', effectiveUserId)
        .single();

      if (profileError) {
        logger.error('Error fetching profile in loadTeamData:', profileError);
        setLoading(false);
        return;
      }

      if (profile?.user_id && profile.user_id !== effectiveUserId) {
        logger.error('User ID mismatch in loadTeamData!', {
          requested: effectiveUserId,
          returned: profile.user_id
        });
        setLoading(false);
        return;
      }

      if (!profile?.team_id) {
        setLoading(false);
        return;
      }

      setTeamId(profile.team_id);

      // Fetch subscription and team profiles in parallel
      const [subResult, teamProfilesResult] = await Promise.all([
        supabase
          .from('team_subscriptions' as any)
          .select('*')
          .eq('team_id', profile.team_id)
          .maybeSingle(),
        supabase
          .from('profiles')
          .select('user_id')
          .eq('team_id', profile.team_id)
      ]);

      const subData = subResult.data;
      const teamProfiles = teamProfilesResult.data;
      const teamUserIds = teamProfiles?.map(p => p.user_id) || [];

      if (subData) {
        setSubscription(subData as any);
      }

      // Calculate start of current calendar month for monthly usage counts
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const monthStartIso = monthStart.toISOString();

      // Fetch plan_limits, norths count, CMA count, and Buyer Report count in parallel
      const [limitsResult, northsResult, cmaResult, propertyReportResult] = await Promise.all([
        subData
          ? supabase
              .from('plan_limits' as any)
              .select('*')
              .eq('plan_tier', (subData as any).plan_tier)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        teamUserIds.length > 0
          ? supabase
              .from('norths')
              .select('*', { count: 'exact', head: true })
              .in('user_id', teamUserIds)
              .eq('is_published', true)
              .eq('is_draft', false)
          : Promise.resolve({ count: 0 }),
        profile.team_id
          ? supabase
              .from('cmas' as any)
              .select('id')
              .eq('team_id', profile.team_id)
              .gte('created_at', monthStartIso)
          : Promise.resolve({ data: [], error: null }),
        profile.team_id
          ? supabase
              .from('property_reports' as any)
              .select('id')
              .eq('team_id', profile.team_id)
              .gte('created_at', monthStartIso)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if ((limitsResult as any).error) {
        logger.error('Error fetching plan limits:', (limitsResult as any).error);
      }
      if ((cmaResult as any).error) {
        logger.error('Error fetching CMA count:', (cmaResult as any).error);
      }
      if ((propertyReportResult as any).error) {
        logger.error('Error fetching property report count:', (propertyReportResult as any).error);
      }
      setPlanLimits((limitsResult as any).data || null);

      const finalUsage: TeamUsage = {
        norths_created: (northsResult as any).count || 0,
        cmas_this_month: ((cmaResult as any).data?.length ?? 0),
        property_reports_this_month: ((propertyReportResult as any).data?.length ?? 0),
      };

      setUsage(finalUsage);
    } catch (error) {
      logger.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const resolveAndLoad = async () => {
      try {
        let userId: string | null = null;

        // For impersonation, use the impersonated user ID directly (no network call)
        if (isImpersonating && impersonatedUser?.id) {
          userId = impersonatedUser.id;
        } else {
          // Use getSession() (cached, no network call) instead of getUser()
          const { data: { session } } = await supabase.auth.getSession();
          userId = session?.user?.id || null;
        }

        if (!mounted) return;
        setCurrentUserId(userId);

        if (previousUserIdRef.current !== null && previousUserIdRef.current !== userId) {
          // User changed — reset state
          setLoading(true);
          setTeamId(null);
          setPlanLimits(null);
          setUsage(null);
          setSubscription(null);
        }

        previousUserIdRef.current = userId;

        if (userId) {
          await loadTeamData(userId);
        } else {
          setLoading(false);
        }
      } catch (error) {
        if (mounted) {
          setCurrentUserId(null);
          setLoading(false);
        }
      }
    };

    resolveAndLoad();

    // Listen only for sign-in/sign-out (not TOKEN_REFRESHED)
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        if (mounted) {
          logger.debug('Auth state changed in useTeamUsage - resetting state', { event });
          setLoading(true);
          setTeamId(null);
          setPlanLimits(null);
          setUsage(null);
          setSubscription(null);
          previousUserIdRef.current = null;
          const newUserId = session?.user?.id || null;
          setCurrentUserId(newUserId);
          if (newUserId) {
            loadTeamData(newUserId);
          } else {
            setLoading(false);
          }
        }
      }
    });

    return () => {
      mounted = false;
      authSubscription.unsubscribe();
    };
  }, [isImpersonating, impersonatedUser?.id]);

  // Entitlement is evaluated from status + period end, not plan_tier alone.
  // While still loading we stay optimistic so the UI doesn't flash a paywall
  // at a paying customer on every page load.
  const entitlement = evaluateEntitlement(subscription);
  const isEntitled = loading ? true : entitlement.entitled;

  const checkLimit = (type: 'norths' | 'team_members'): boolean => {
    if (!planLimits || !usage) return false;
    if (!isEntitled) return false;

    switch (type) {
      case 'norths':
        if (planLimits.max_active_norths === null) return true;
        return usage.norths_created < planLimits.max_active_norths;

      case 'team_members':
        return true;

      default:
        return false;
    }
  };

  const canCreateCMA = (): boolean => {
    if (!planLimits || !usage) return true; // Optimistic while loading
    if (!isEntitled) return false;
    if (planLimits.max_cmas_per_month === null) return true; // Unlimited
    return usage.cmas_this_month < planLimits.max_cmas_per_month;
  };

  const canCreateBuyerReport = (): boolean => {
    if (!planLimits || !usage) return false;
    if (!isEntitled) return false;
    if (planLimits.max_property_reports_per_month == null) return true; // unlimited
    if (planLimits.max_property_reports_per_month === 0) return false;  // locked (0 = no access)
    return usage.property_reports_this_month < planLimits.max_property_reports_per_month;
  };

  const incrementUsage = async (type: 'norths', amount: number = 1) => {
    if (currentUserId) {
      await loadTeamData(currentUserId);
    }
  };

  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string>('');

  const showUpgradePrompt = (feature: string) => {
    setUpgradeFeature(feature);
    setUpgradeDialogOpen(true);
  };

  const getUsagePercentage = (type: 'norths'): number => {
    if (!planLimits || !usage) return 0;

    switch (type) {
      case 'norths':
        if (planLimits.max_active_norths === null) return 0;
        return (usage.norths_created / planLimits.max_active_norths) * 100;

      default:
        return 0;
    }
  };

  return {
    loading,
    teamId,
    planLimits,
    usage,
    subscription,
    isEntitled,
    entitlement,
    checkLimit,
    canCreateCMA,
    canCreateBuyerReport,
    incrementUsage,
    showUpgradePrompt,
    getUsagePercentage,
    refreshUsage: () => currentUserId ? loadTeamData(currentUserId) : Promise.resolve(),
    upgradeDialogOpen,
    setUpgradeDialogOpen,
    upgradeFeature,
  };
}
